import { CircleCancelMinor } from '@shopify/polaris-icons'
import React, { memo, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { CollectedDataStatus, CollectedDataStepType } from 'src/constants/collectedData'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { ContractorStepDataComponent } from './ContractorStepData'
import { ContractorStepDocuments, FULL_PACKAGE_FILE_NAME } from './ContractorStepDocuments'
import { ContractorStepHeader } from './ContractorStepHeader'
import { DownloadAllButton } from './DownloadAllButton'
import ApproveButton from './step-actions/ApproveButton'
import DeclineButton from './step-actions/DeclineButton'
import { useSelector } from 'react-redux'
import { getPartnerId } from 'src/selectors/user'
import { useContractorStepDetailsBlocksVisibility } from '../contractorStepDetailsCard/hooks/useContractorStepDetailsBlocksVisibility'
import { useContractorStepItemState } from '../contractorStepDetailsCard/hooks/useContractorStepItemState'
import { SubmissionBlock } from '../contractorStepDetailsCard/types'
import { DECLINED_STATUS_LABEL, statusLabels } from '../contractorStepDetailsCard/constants'
import { isApprovalQueueCardStatus } from '../contractorStepDetailsCard/utils'
import { isAwaitingInternalApprovalAfterCustomer } from 'src/utils/collectedData'
import { ContractorStepDetailsDeclineReasonBlock } from '../contractorStepDetailsCard/components/ContractorStepDetailsDeclineReasonBlock'

export interface ContractorStepDetailsModalProps {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  delayed?: boolean
  isCombinedStep?: boolean
  isRequestApprove?: boolean
  isSubcontractor?: boolean
  contentBlock: SubmissionBlock
  onRefreshContractors: () => void | Promise<void>
  onClose: () => void
}

const ContractorStepDetailsModalComponent = ({
  collectedData,
  contractorDetails,
  delayed,
  isCombinedStep = false,
  isRequestApprove,
  isSubcontractor = false,
  contentBlock,
  onRefreshContractors,
  onClose,
}: ContractorStepDetailsModalProps) => {
  const partnerId = useSelector(getPartnerId)

  const { stepCanBeRecollected, stepStatus } = useContractorStepItemState({
    collectedData,
    contractorDetails,
    isRequestApprove,
    isSubcontractor,
  })

  const visibility = useContractorStepDetailsBlocksVisibility({
    collectedData,
    contractorDetails,
    stepCanBeRecollected,
    stepStatus,
    partnerId,
    isRequestApprove,
    isSubcontractor,
  })
  const showStepDataComponent = visibility.showStepDataComponent
  const showDetails = visibility.showDetails

  const baseStatusLabel = statusLabels[contentBlock.status]
  const declineReason = contentBlock.collectedData?.lastRejectionReason
  const isDeclinedStep = !!(
    declineReason &&
    !isApprovalQueueCardStatus(contentBlock.status) &&
    !isAwaitingInternalApprovalAfterCustomer(contentBlock.collectedData)
  )
  const statusLabel = isDeclinedStep ? DECLINED_STATUS_LABEL : baseStatusLabel
  const portalRoot = document.getElementById('step-details')
  const documents = collectedData.documents?.length
    ? collectedData.documents
    : collectedData.lastRejectionDocuments || []

  const isSignContract = collectedData.collectDataStep.type === CollectedDataStepType.SignContract

  const fullPackageDocument = useMemo(
    () =>
      isSignContract ? documents.find((d) => d.fileName === FULL_PACKAGE_FILE_NAME) : undefined,
    [documents, isSignContract],
  )

  if (!portalRoot) {
    return null
  }

  return createPortal(
    <div
      css={tw`rounded-2xl shadow-lg bg-white py-4 min-w-[380px] flex flex-col pointer-events-auto overflow-y-auto h-full w-full`}
    >
      <div css={tw`px-4`}>
        <ContractorStepHeader
          collectedData={collectedData}
          delayed={delayed}
          blockStatus={contentBlock.status}
          blockStatusLabel={statusLabel}
          isDeclinedStep={isDeclinedStep}
          showExpirationDate={visibility.showExpirationDate}
          showEffectiveDate={visibility.showEffectiveDate}
          onRefreshContractors={onRefreshContractors}
        />
        <div css={tw`w-full mt-4 h-[1px] bg-[#EDEDED]`} />
      </div>
      <div css={tw`flex-1 overflow-y-auto px-4`}>
        {isDeclinedStep && declineReason && (
          <div css={tw`-mx-4 mb-0 mt-4`}>
            <ContractorStepDetailsDeclineReasonBlock declineReason={declineReason} />
          </div>
        )}
        {showStepDataComponent && (
          <ContractorStepDataComponent
            collectedData={collectedData}
            contractorDetails={contractorDetails}
            partnerId={partnerId}
          />
        )}
        {!!documents?.length &&
          collectedData.collectDataStep.type !== CollectedDataStepType.PIICollection && (
            <ContractorStepDocuments
              collectedData={collectedData}
              partnerDriverId={contractorDetails.id}
              header={
                <ContractorStepHeader
                  collectedData={collectedData}
                  delayed={delayed}
                  blockStatus={contentBlock.status}
                  blockStatusLabel={statusLabel}
                  isDeclinedStep={isDeclinedStep}
                  showExpirationDate={visibility.showExpirationDate}
                  showEffectiveDate={visibility.showEffectiveDate}
                  onRefreshContractors={onRefreshContractors}
                />
              }
            />
          )}
      </div>

      <div css={tw`flex justify-end mt-4 flex-col gap-4 px-4`}>
        {fullPackageDocument && (
          <DownloadAllButton
            document={fullPackageDocument}
            partnerDriverId={contractorDetails.id}
            stepId={fullPackageDocument.collectDataStepId ?? collectedData.collectDataStep.id}
          />
        )}
        {!isRequestApprove &&
          showDetails &&
          collectedData.status === CollectedDataStatus.WaitingForApproval && (
            <div css={tw`grid grid-cols-2 gap-2 w-full`}>
              <ApproveButton
                contractorDetails={contractorDetails}
                collectedData={collectedData}
                isCombinedStep={isCombinedStep}
                onAction={onRefreshContractors}
              />
              <DeclineButton
                contractorDetails={contractorDetails}
                collectedData={collectedData}
                onAction={onRefreshContractors}
              />
            </div>
          )}
        <Button
          fullWidth
          color="white"
          size="small"
          onClick={onClose}
          endIcon={<CircleCancelMinor width={20} />}
        >
          Close
        </Button>
      </div>
    </div>,
    portalRoot,
  )
}

export const ContractorStepDetailsModal = memo(ContractorStepDetailsModalComponent)
